import type { ConsiderationStatus, Recommendation } from '@/api/vocabularies';

/**
 * The renderings `DESIGN.md` §3 fixes for every status and every recommendation.
 *
 * `dot` is the mark, `text` the word's colour, `pill` the filled treatments. Everything that is
 * merely a *state* rather than a *judgement* — draft, new, pending — stays neutral ink: "a
 * five-colour status palette is how an interface starts shouting".
 *
 * ⚠️ **The keys are the two schema vocabularies, and the `Record` is what keeps them so.** A
 * `Record<ConsiderationStatus | Recommendation, …>` must carry a key for every member of both and
 * may carry no other, so a status or a verdict added on the backend stops `tsc` here the moment
 * `schema.d.ts` is regenerated — rather than rendering `undefined` into `style.pill`, which throws
 * and takes the screen with it. `candidates.test.tsx` compares these keys against the enums in
 * `openapi.snapshot.json` as well, so a refreshed snapshot fails the suite even before the types are
 * regenerated.
 *
 * It lives beside the component rather than inside it because `react-refresh/only-export-components`
 * refuses a non-component export from a component file — the same reason `buttonVariants.ts` and
 * `scoreScale.ts` are their own modules.
 */
export const statusDotStyles: Record<
  ConsiderationStatus | Recommendation,
  { dot?: string; text: string; pill?: string }
> = {
  NEW: { dot: 'border border-rule-control bg-transparent', text: 'text-ink-500' },
  INTERVIEWING: { dot: 'bg-ink-900', text: 'text-ink-900' },
  OFFER: { dot: 'bg-positive', text: 'text-ink-900' },
  HIRED: { text: 'text-positive', pill: 'bg-positive/[0.12]' },
  REJECTED: { dot: 'bg-negative', text: 'text-ink-500' },
  STRONG_YES: { text: 'text-positive', pill: 'bg-positive-wash' },
  YES: { dot: 'bg-positive', text: 'text-positive' },
  NO: { dot: 'bg-negative', text: 'text-negative' },
  STRONG_NO: { text: 'text-negative', pill: 'bg-negative-wash' },
};
